// Client du studio : réponses validées et cache limité au compte et à la galerie.
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  texteStudioSchema,
  listeStudioSchema,
  type CreationStudio,
  type FiltresStudio,
} from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { useMutation } from './mutation.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

const reponse = z.object({
  texte: texteStudioSchema,
  contenu: z.string().optional(),
  postId: z.string().optional(),
});

const chemin = (g: string, suite = '') => `/g/${encodeURIComponent(g)}/studio${suite}`;

export const studioApi = {
  liste: async (g: string, f: FiltresStudio, appel: AppelApi) => {
    const q = new URLSearchParams(
      Object.entries(f)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    );
    return listeStudioSchema.parse(await api.get(`${chemin(g)}?${q}`, { galerie: g, ...appel }));
  },
  fiche: async (g: string, id: string, appel: AppelApi) =>
    reponse.parse(await api.get(chemin(g, `/${id}`), { galerie: g, ...appel })),
  creer: async (g: string, corps: CreationStudio, appel: AppelApi) =>
    reponse.parse(await api.post(chemin(g), corps, { galerie: g, ...appel })),
  geste: async (g: string, id: string, geste: string, corps: object, appel: AppelApi) =>
    reponse.parse(await api.post(chemin(g, `/${id}/${geste}`), corps, { galerie: g, ...appel })),
};

export function useTextesStudio(g: string, filtres: FiltresStudio) {
  return useQuery({
    queryKey: cleMetier(g, 'studio', 'liste', filtres),
    queryFn: (c) => studioApi.liste(g, filtres, appelDeRequete(c)),
  });
}

export function useTexteStudio(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'studio', id),
    queryFn: (c) => studioApi.fiche(g, id, appelDeRequete(c)),
  });
}

export function useSourcesStudio(
  g: string,
  type: CreationStudio['source']['type'],
  q: string,
  selection?: string,
) {
  return useQuery({
    queryKey: cleMetier(g, 'studio', 'sources', type, q, selection),
    enabled: type !== 'libre',
    queryFn: async (c) =>
      z
        .object({
          sources: z.array(
            z.object({
              id: z.string(),
              libelle: z.union([
                z.string(),
                z.object({ fr: z.string(), en: z.string().optional() }),
              ]),
            }),
          ),
        })
        .parse(
          await api.get(
            `${chemin(g, '/sources')}?${new URLSearchParams({ type, q, ...(selection ? { selection } : {}) })}`,
            {
              galerie: g,
              ...appelDeRequete(c),
            },
          ),
        ),
  });
}

export function useGesteStudio(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        v: { creation: CreationStudio } | { id: string; geste: string; corps: object },
        appel,
      ) =>
        'creation' in v
          ? studioApi.creer(g, v.creation, appel)
          : studioApi.geste(g, v.id, v.geste, v.corps, appel),
      onSuccess: (r) => client.setQueryData(cleMetier(g, 'studio', r.texte.id), r),
      onSettled: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'studio') });
        void client.invalidateQueries({ queryKey: cleMetier(g, 'ia') });
      },
    }),
  );
}
